import { Menu, useResourceDefinitions, useSidebarState, usePermissions } from 'react-admin';
import { Box, Typography, Divider } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { tokens } from '../theme.js';

/**
 * Group resources into sections so the long flat menu becomes scannable.
 * Resources not listed here render in "Other" at the bottom.
 */
const SECTIONS = [
  { label: 'Homepage', resources: ['home-welcome', 'home-popup', 'slides', 'stats', 'home-advantage', 'features', 'level-cards', 'leadership-messages', 'partners', 'campuses', 'facilities'] },
  { label: 'Editorial', resources: ['news', 'events', 'achievements'] },
  { label: 'Media', resources: ['gallery-albums', 'gallery-images'] },
  { label: 'Content', resources: ['pages'] },
  { label: 'People', resources: ['faculty', 'alumni', 'addresses'] },
  { label: 'Operations', resources: ['calendar', 'careers'] },
  { label: 'Inbox', resources: ['admission-enquiries', 'alumni-registrations', 'contact-submissions', 'newsletter-subscribers'] },
  { label: 'Settings', resources: ['site-settings', 'admission-classes', 'menu-items', 'users', 'activity-logs'], superOnly: true },
];

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      px: 2,
      pt: 1.5,
      pb: 0.5,
      display: 'block',
      color: tokens.ink4,
      fontSize: 10,
      letterSpacing: '0.18em',
      fontWeight: 700,
    }}
  >
    {children}
  </Typography>
);

export const MlisMenu = () => {
  const { permissions } = usePermissions();
  const isSuper = permissions === 'super_admin';
  const resources = useResourceDefinitions();
  const [open] = useSidebarState();

  // Resources React Admin actually registered (i.e. visible to this role)
  const available = new Set(Object.keys(resources));

  // Build sections, dropping resources the current role can't see
  const visibleSections = SECTIONS
    .filter((s) => !s.superOnly || isSuper)
    .map((s) => ({ ...s, resources: s.resources.filter((r) => available.has(r)) }))
    .filter((s) => s.resources.length > 0);

  // Catch any registered resource not listed in SECTIONS
  const grouped = new Set(visibleSections.flatMap((s) => s.resources));
  const orphans = Object.keys(resources).filter((r) => !grouped.has(r));

  return (
    <Menu>
      <Box sx={{ pt: 1, pb: 4 }}>
        <Menu.Item to="/help" primaryText="Help & guide" leftIcon={<HelpOutlineIcon />} />
        <Divider sx={{ my: 0.5, mx: 2, borderColor: tokens.line2 }} />
        {visibleSections.map((section, i) => (
          <Box key={section.label}>
            {i > 0 && <Divider sx={{ my: 0.5, mx: 2, borderColor: tokens.line2 }} />}
            {open && <SectionLabel>{section.label}</SectionLabel>}
            {section.resources.map((name) => {
              const def = resources[name];
              if (!def) return null;
              return (
                <Menu.ResourceItem
                  key={name}
                  name={name}
                  primaryText={def.options?.label || def.recordRepresentation || name}
                />
              );
            })}
          </Box>
        ))}
        {orphans.length > 0 && (
          <Box>
            <Divider sx={{ my: 0.5, mx: 2, borderColor: tokens.line2 }} />
            {open && <SectionLabel>Other</SectionLabel>}
            {orphans.map((name) => (
              <Menu.ResourceItem key={name} name={name} />
            ))}
          </Box>
        )}
      </Box>
    </Menu>
  );
};
